import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Select } from '@creditornot/cb-select';
import { Interval } from 'luxon';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';

import { CustomerType } from '../../../apis/merchant-gateway/insights';
import ErrorPage from '../components/ErrorPage';
import ErrorView from '../components/ErrorView';
import { SummaryCard } from '../components/InsightSummary/SummaryTable';
import LoadingPage from '../components/LoadingPage';
import LoadingView from '../components/LoadingView';
import CustomLegends from '../components/charts/CustomLegends';
import { LegendChartType } from '../components/charts/types';
import BasketSizeChart from '../components/customer-base/BasketSizeChart';
import CustomerBaseChart from '../components/customer-base/CustomerBaseChart';
import CustomerBaseSummary from '../components/customer-base/CustomerBaseSummary';
import { useBasketSizeInsight, usePerformanceInsightWithPreviousInterval } from '../hooks/useInsightApi';
import { useTelemetryBusinessInsightsV2CustomerBaseView } from '../telemetry';
import { InsightEntity, OrderType } from '../types';
import { isAllError, isInitialLoading } from '../utils';

type CustomerBasePageProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  isVATEnabled: boolean;
};

enum CustomerChartCategory {
  REGULAR_VS_WOLT_PLUS = 'REGULAR_VS_WOLT_PLUS',
  NEW_VS_RETURNING = 'NEW_VS_RETURNING',
}

const Root = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  padding: 24px;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
    gap: 16px;
  }
`;

const CustomerBaseSummaryContainer = styled.div`
  position: relative;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;

  display: flex;
  flex-direction: column;
  min-height: 365px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  gap: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const BasketSizeChartWrapper = styled.div`
  padding: 16px 0;
`;

const ChartSelector = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-block-end: 24px;
`;

const SelectWrapper = styled.div`
  min-width: 208px;
`;

const CustomerBaseChartWrapper = styled.div`
  padding: 16px 24px;
`;

const BasketChartsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-wrap: wrap;
  }
`;

const chartCategoryToNameMap = {
  [CustomerChartCategory.REGULAR_VS_WOLT_PLUS]: 'insights.customer-base.regular-vs-wolt-plus',
  [CustomerChartCategory.NEW_VS_RETURNING]: 'insights.customer-base.new-vs-returning',
};

const CustomerBasePage = ({ insightEntity, dateInterval, isVATEnabled }: CustomerBasePageProps) => {
  const performanceInsight = usePerformanceInsightWithPreviousInterval({ insightEntity, dateInterval });
  const basketSizeInsight = useBasketSizeInsight(insightEntity, dateInterval);
  const { formatMessage } = useIntl();
  const isWoltPlusEnabled = insightEntity.isWoltPlus;
  useTelemetryBusinessInsightsV2CustomerBaseView(insightEntity.type);
  const chartOptions = useMemo(
    () => [
      CustomerChartCategory.NEW_VS_RETURNING,
      ...(isWoltPlusEnabled ? [CustomerChartCategory.REGULAR_VS_WOLT_PLUS] : []),
    ],
    [isWoltPlusEnabled],
  );

  const [selectedCategory, setSelectedCategory] = useState<CustomerChartCategory>(chartOptions[0]);

  const chartOptionsProps = useMemo(
    () =>
      chartOptions.map(value => ({
        label: formatMessage({ id: chartCategoryToNameMap[value] }),
        value,
      })),
    [chartOptions, formatMessage],
  );

  const handleCategoryChange = (value: CustomerChartCategory) => {
    setSelectedCategory(value);
    // todo @ivanmartynov check with Maria about adding telemetry https://www.figma.com/file/aR08aNX6c1Bw0hGvv8vQXM?node-id=6245:29688#466500455
    // telemetrySelectPerformanceChartInHomeInsightPage(chartCategoryToTelemetryEventMap[value]);
  };

  const ordersLegends = {
    [CustomerChartCategory.NEW_VS_RETURNING]: {
      [CustomerType.RETURNING]: {
        value: formatMessage({ id: 'insights.customer-base.returning' }),
        color: colors.wolt88,
        type: LegendChartType.BAR,
      },
      [CustomerType.NEW]: {
        value: formatMessage({ id: 'insights.customer-base.new' }),
        color: colors.wolt32,
        type: LegendChartType.BAR,
      },
    },
    [CustomerChartCategory.REGULAR_VS_WOLT_PLUS]: {
      [OrderType.Regular]: {
        value: formatMessage({ id: 'insights.customer-base.regular-order' }),
        color: colors.wolt88,
        type: LegendChartType.BAR,
      },
      ...(isWoltPlusEnabled
        ? {
            [OrderType.WoltPlus]: {
              value: formatMessage({ id: 'insights.customer-base.woltplus-order' }),
              color: colors.wolt32,
              type: LegendChartType.BAR,
            },
          }
        : {}),
    },
  };

  const allInsightResponses = [performanceInsight, basketSizeInsight];

  if (isInitialLoading(allInsightResponses)) {
    return <LoadingPage />;
  }
  if (isAllError(allInsightResponses)) {
    return (
      <ErrorPage
        onRetry={() => {
          allInsightResponses.forEach(insight => insight.refetch());
        }}
      />
    );
  }

  const performanceSelectedTotal = performanceInsight.selected.data?.data.subscription.total;
  const performancePreviousTotal = performanceInsight.previous.data?.data.subscription.total;
  const performanceSplitData = performanceInsight.selected.data?.data.subscription.split;

  return (
    <Root>
      <CustomerBaseSummaryContainer>
        {performanceInsight.isError ? (
          <ErrorView onRetry={performanceInsight.refetch} />
        ) : performanceInsight.isLoading || performanceInsight.isFetching ? (
          <LoadingView />
        ) : (
          <>
            <CardHeader>
              {performanceSelectedTotal && (
                <CustomerBaseSummary
                  customersTotalForSelected={performanceSelectedTotal}
                  customersTotalForPrevious={performancePreviousTotal}
                />
              )}

              <CustomLegends legends={Object.values(ordersLegends[selectedCategory])} />
            </CardHeader>
            <CustomerBaseChartWrapper>
              {chartOptions.length > 1 && (
                <ChartSelector>
                  <T.Body3>
                    <FormattedMessage id="insights.performance.show-chart-for" />
                  </T.Body3>
                  <SelectWrapper>
                    <Select
                      value={selectedCategory}
                      options={chartOptionsProps}
                      onChange={handleCategoryChange}
                      disableSearch
                    />
                  </SelectWrapper>
                </ChartSelector>
              )}
              <CustomerBaseChart
                legends={ordersLegends[selectedCategory]}
                data={performanceSplitData || []}
                summaryKey="insights.customer-base.total-customers"
              />
            </CustomerBaseChartWrapper>
          </>
        )}
      </CustomerBaseSummaryContainer>
      <BasketChartsContainer>
        <SummaryCard
          isError={basketSizeInsight.isError || (basketSizeInsight.isLoading && !basketSizeInsight.isFetching)}
          onRetry={basketSizeInsight.refetch}
          isFetching={basketSizeInsight.isFetching}
          title={formatMessage({ id: 'insights.customer-base.basket-size-value.title' })}
          tooltip={formatMessage({ id: 'insights.customer-base.basket-size-value.tooltip' })}
        >
          <BasketSizeChartWrapper>
            <BasketSizeChart
              data={basketSizeInsight.data?.data.basketSize}
              summaryKey="insights.customers.basket-size-value"
              currency={basketSizeInsight.data?.currency}
              basketValueType="value"
              isVATEnabled={isVATEnabled}
            />
          </BasketSizeChartWrapper>
        </SummaryCard>
        <SummaryCard
          isError={basketSizeInsight.isError || (basketSizeInsight.isLoading && !basketSizeInsight.isFetching)}
          onRetry={basketSizeInsight.refetch}
          isFetching={basketSizeInsight.isFetching}
          title={formatMessage({ id: 'insights.customer-base.basket-size-items.title' })}
          tooltip={formatMessage({ id: 'insights.customer-base.basket-size-items.tooltip' })}
        >
          <BasketSizeChartWrapper>
            <BasketSizeChart
              data={basketSizeInsight.data?.data.basketSize}
              summaryKey="insights.customers.basket-size-items"
              basketValueType="items"
              isVATEnabled={isVATEnabled}
            />
          </BasketSizeChartWrapper>
        </SummaryCard>
      </BasketChartsContainer>
    </Root>
  );
};

export default CustomerBasePage;
